import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { api } from '@/services/api'

export type RewardKind = 'lesson' | 'quiz' | 'course' | 'certificate' | 'journey'

export type RewardEvent = {
  id: string
  kind: RewardKind
  title: string
  body?: string
  points?: number | null
  score?: number | null
  ctaLabel?: string
  ctaTo?: string
  persistKey?: string
}

const SEEN_STORAGE_KEY = 'lms.rewards.seen.v1'

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? new Set(parsed.filter((item) => typeof item === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

function persistSeen(seen: Set<string>): void {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...seen]))
  } catch {
    // Private mode or quota — celebrations still work for this session.
  }
}

let nextId = 1

export const useRewardsStore = defineStore('rewards', () => {
  const queue = ref<RewardEvent[]>([])
  const seen = ref<Set<string>>(loadSeen())
  const totalPoints = ref<number | null>(null)
  const afterClear = ref<Array<() => void>>([])

  const current = computed(() => queue.value[0] ?? null)

  function hasSeen(key: string): boolean {
    return seen.value.has(key)
  }

  function markSeen(key: string): void {
    if (seen.value.has(key)) return
    const next = new Set(seen.value)
    next.add(key)
    seen.value = next
    persistSeen(next)
  }

  function enqueue(event: Omit<RewardEvent, 'id'>): boolean {
    if (event.persistKey && hasSeen(event.persistKey)) return false

    if (event.persistKey) markSeen(event.persistKey)

    queue.value = [
      ...queue.value,
      {
        ...event,
        id: `reward-${nextId++}`,
      },
    ]
    return true
  }

  function flushAfterClear(): void {
    const callbacks = afterClear.value
    afterClear.value = []
    for (const callback of callbacks) callback()
  }

  function dismiss(): void {
    queue.value = queue.value.slice(1)
    if (queue.value.length === 0) flushAfterClear()
  }

  /** Runs immediately if nothing is showing; otherwise after the last overlay. */
  function onQueueClear(callback: () => void): void {
    if (queue.value.length === 0) {
      callback()
      return
    }
    afterClear.value = [...afterClear.value, callback]
  }

  async function refreshPoints(): Promise<void> {
    try {
      const data = await api<{ total_points: number }>('/points/mine')
      totalPoints.value = data.total_points ?? 0
    } catch {
      // Points are decorative; a failed fetch should not break the overlay.
    }
  }

  return {
    queue,
    current,
    totalPoints,
    hasSeen,
    enqueue,
    dismiss,
    onQueueClear,
    refreshPoints,
  }
})
